import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  ListItemButton,
  ListItemText,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import TuneIcon from "@mui/icons-material/Tune";
import {
  ACTIVE_STATUSES,
  ATTENTION_NEEDED_STATUSES,
  TERMINAL_STATUSES,
  SEVERITY_META,
} from "../common/statusColors";
import {
  filterActiveChipSx,
  filterListItemSx,
  filterListItemTextProps,
  filterPopoverPaperSx,
  filterScrollListSx,
  filterSearchFieldSx,
  filterTriggerSx,
  filterTriggerLabelSx,
  filterTriggerEllipsisSx,
  filterToolbarSx,
  filterSegmentSelectedSx,
  filterResetButtonSx,
  FILTER_POPOVER_WIDTH,
  HUB_MUTED,
} from "../common/filterBarStyles";

const SEVERITIES = ["CRITICAL", "HIGH", "MEDIUM", "LOW"];

const STATUS_PRESETS = [
  { key: "active", label: "Active" },
  { key: "attention", label: "Needs Attention" },
  { key: "terminal", label: "Terminal" },
];

const FilterTrigger = ({ label, count, icon, onClick }) => {
  const text = count != null && count > 0 ? `${label} (${count})` : label;
  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={icon}
      onClick={onClick}
      title={text}
      sx={{ ...filterTriggerSx, ...filterTriggerEllipsisSx }}
    >
      <Box component="span" sx={filterTriggerLabelSx}>
        {text}
      </Box>
    </Button>
  );
};

const FilterPopover = ({ open, anchorEl, onClose, width = FILTER_POPOVER_WIDTH, children }) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    transformOrigin={{ vertical: "top", horizontal: "left" }}
    slotProps={{ paper: { sx: filterPopoverPaperSx(width) } }}
  >
    {children}
  </Popover>
);

const SingleSelectRow = ({ label, selected, onClick, adornment }) => (
  <ListItemButton dense selected={selected} onClick={onClick} sx={filterListItemSx}>
    {adornment}
    <ListItemText primary={label} primaryTypographyProps={filterListItemTextProps} sx={{ m: 0 }} />
    {selected && <CheckIcon sx={{ fontSize: 16, color: "primary.main", ml: "auto" }} />}
  </ListItemButton>
);

const SeverityDot = ({ severity }) => {
  const meta = SEVERITY_META[severity] || { fg: "#9e9e9e" };
  return (
    <Box
      sx={{
        width: 8,
        height: 8,
        borderRadius: "50%",
        backgroundColor: meta.fg,
        flexShrink: 0,
        mr: 1,
      }}
    />
  );
};

/**
 * Top-of-list filter and search bar. Filter chips map directly to API query
 * params; selecting a chip mutates the parent's filter state which is then
 * synced into the URL so the view is shareable.
 */
const FilterBar = ({
  filters,
  onChange,
  onSearchChange,
  onReset,
  searchValue = "",
  onSearchSubmit,
  onNewRequest,
  remediableCheckIds = [],
  accountOptions = [],
  accountsLoading = false,
  serviceOptions = [],
  onRefresh,
  refreshing,
}) => {
  const [severityAnchor, setSeverityAnchor] = useState(null);
  const [serviceAnchor, setServiceAnchor] = useState(null);
  const [accountAnchor, setAccountAnchor] = useState(null);

  const statusSet = useMemo(
    () => new Set((filters.status_in || "").split(",").filter(Boolean)),
    [filters.status_in]
  );

  const isStatusPresetSelected = (key) => {
    const preset =
      key === "attention"
        ? ATTENTION_NEEDED_STATUSES
        : key === "active"
          ? ACTIVE_STATUSES
          : key === "terminal"
            ? Array.from(TERMINAL_STATUSES)
            : null;
    if (!preset || statusSet.size !== preset.length) return false;
    return preset.every((status) => statusSet.has(status));
  };

  const applyStatusPreset = (preset) => {
    if (preset !== "clear" && isStatusPresetSelected(preset)) {
      onChange({ status_in: null });
      return;
    }
    if (preset === "attention") onChange({ status_in: ATTENTION_NEEDED_STATUSES.join(",") });
    else if (preset === "active") onChange({ status_in: ACTIVE_STATUSES.join(",") });
    else if (preset === "terminal") onChange({ status_in: Array.from(TERMINAL_STATUSES).join(",") });
    else onChange({ status_in: null });
  };

  const activeFilterChips = [];
  if (filters.severity)
    activeFilterChips.push({
      key: "severity",
      label: `Severity: ${filters.severity}`,
      clear: () => onChange({ severity: null }),
    });
  if (filters.service)
    activeFilterChips.push({
      key: "service",
      label: `Service: ${filters.service}`,
      clear: () => onChange({ service: null }),
    });
  if (filters.account)
    activeFilterChips.push({
      key: "account",
      label: `Account: ${filters.account}`,
      clear: () => onChange({ account: null }),
    });
  if (filters.check)
    activeFilterChips.push({
      key: "check",
      label: `Check: ${filters.check}`,
      clear: () => onChange({ check: null }),
    });

  return (
    <Box sx={{ mb: 2, fontSize: 13 }}>
      <Box sx={filterToolbarSx}>
        <TextField
          size="small"
          placeholder="Search check, resource, submitted by…"
          value={searchValue}
          onChange={(e) => onSearchChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onSearchSubmit?.();
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: HUB_MUTED }} />
              </InputAdornment>
            ),
            endAdornment: searchValue ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    onSearchChange("");
                    onSearchSubmit?.("");
                  }}
                >
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={filterSearchFieldSx}
        />

        <FilterTrigger
          label={filters.severity ? `Severity: ${filters.severity}` : "Severity"}
          icon={<TuneIcon sx={{ fontSize: 18 }} />}
          onClick={(e) => setSeverityAnchor(e.currentTarget)}
        />
        <FilterPopover
          open={Boolean(severityAnchor)}
          anchorEl={severityAnchor}
          onClose={() => setSeverityAnchor(null)}
          width={220}
        >
          <Box sx={{ px: 0.5, py: 0.5 }}>
            <SingleSelectRow
              label="All severities"
              selected={!filters.severity}
              onClick={() => {
                onChange({ severity: null });
                setSeverityAnchor(null);
              }}
            />
            {SEVERITIES.map((s) => (
              <SingleSelectRow
                key={s}
                label={s}
                selected={filters.severity === s}
                adornment={<SeverityDot severity={s} />}
                onClick={() => {
                  onChange({ severity: s });
                  setSeverityAnchor(null);
                }}
              />
            ))}
          </Box>
        </FilterPopover>

        {serviceOptions.length > 0 && (
          <>
            <FilterTrigger
              label={filters.service ? `Service: ${filters.service}` : "Service"}
              icon={<TuneIcon sx={{ fontSize: 18 }} />}
              onClick={(e) => setServiceAnchor(e.currentTarget)}
            />
            <FilterPopover
              open={Boolean(serviceAnchor)}
              anchorEl={serviceAnchor}
              onClose={() => setServiceAnchor(null)}
              width={200}
            >
              <Box sx={{ px: 0.5, py: 0.5 }}>
                <SingleSelectRow
                  label="All services"
                  selected={!filters.service}
                  onClick={() => {
                    onChange({ service: null });
                    setServiceAnchor(null);
                  }}
                />
                {serviceOptions.map((s) => (
                  <SingleSelectRow
                    key={s}
                    label={s}
                    selected={filters.service === s}
                    onClick={() => {
                      onChange({ service: s });
                      setServiceAnchor(null);
                    }}
                  />
                ))}
              </Box>
            </FilterPopover>
          </>
        )}

        <>
          <FilterTrigger
            label={
              filters.account
                ? `Account: ${accountOptions.find((a) => a.value === filters.account)?.label || filters.account}`
                : "Account"
            }
            icon={<TuneIcon sx={{ fontSize: 18 }} />}
            onClick={(e) => setAccountAnchor(e.currentTarget)}
          />
          <FilterPopover
            open={Boolean(accountAnchor)}
            anchorEl={accountAnchor}
            onClose={() => setAccountAnchor(null)}
            width={FILTER_POPOVER_WIDTH}
          >
            {accountsLoading && accountOptions.length === 0 ? (
              <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: "text.secondary" }}>
                Loading accounts…
              </Typography>
            ) : accountOptions.length === 0 ? (
              <Typography sx={{ px: 2, py: 1.5, fontSize: 13, color: "text.secondary" }}>
                No accounts loaded. Open Configuration → Cloud Accounts, then return here.
              </Typography>
            ) : (
              <Box sx={{ ...filterScrollListSx, maxHeight: 240 }}>
                <SingleSelectRow
                  label="All accounts"
                  selected={!filters.account}
                  onClick={() => {
                    onChange({ account: null });
                    setAccountAnchor(null);
                  }}
                />
                {accountOptions.map((a) => (
                  <SingleSelectRow
                    key={a.value}
                    label={a.label}
                    selected={filters.account === a.value}
                    onClick={() => {
                      onChange({ account: a.value });
                      setAccountAnchor(null);
                    }}
                  />
                ))}
              </Box>
            )}
          </FilterPopover>
        </>

        {STATUS_PRESETS.map((p) => {
          const selected = isStatusPresetSelected(p.key);
          return (
            <Button
              key={p.key}
              variant="outlined"
              size="small"
              aria-pressed={selected}
              onClick={() => applyStatusPreset(p.key)}
              sx={{
                ...filterTriggerSx,
                ...(selected ? filterSegmentSelectedSx : {}),
              }}
            >
              {p.label}
            </Button>
          );
        })}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto", flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            onClick={onRefresh}
            disabled={refreshing}
            startIcon={
              <RefreshIcon
                sx={{
                  fontSize: 18,
                  animation: refreshing ? "spin 1s linear infinite" : "none",
                  "@keyframes spin": {
                    from: { transform: "rotate(0deg)" },
                    to: { transform: "rotate(360deg)" },
                  },
                }}
              />
            }
            sx={filterResetButtonSx}
          >
            Refresh
          </Button>

          {onNewRequest && (
            <Button
              variant="contained"
              size="small"
              onClick={onNewRequest}
              sx={{
                ...filterTriggerSx,
                ...filterSegmentSelectedSx,
              }}
            >
              New task
            </Button>
          )}
        </Box>
      </Box>

      {activeFilterChips.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
          {activeFilterChips.map((c) => (
            <Chip
              key={c.key}
              label={c.label}
              size="small"
              onDelete={c.clear}
              sx={filterActiveChipSx}
            />
          ))}
          <Button
            size="small"
            onClick={onReset}
            sx={{ ml: 0.5, fontSize: 12, fontWeight: 500, textTransform: "none", minHeight: 26 }}
          >
            Clear all
          </Button>
        </Stack>
      )}
    </Box>
  );
};

export default FilterBar;
